import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Eye, EyeOff, Lock, Sparkles, ShieldCheck, Gauge, CreditCard, Crown, Gem } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

const LEVEL_BADGE = {
  standard: { label: 'Standard', icon: CreditCard, className: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100' },
  gold: { label: 'Gold', icon: Sparkles, className: 'bg-gradient-to-r from-yellow-300 to-amber-500 text-amber-900' },
  platinum: { label: 'Platinum', icon: Crown, className: 'bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900' },
  black: { label: 'Black', icon: Gem, className: 'bg-gradient-to-r from-slate-800 to-black text-amber-300 ring-1 ring-amber-400/40' },
  basic: { label: 'Basic', icon: CreditCard, className: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100' },
  premium: { label: 'Premium', icon: Sparkles, className: 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white' },
  elite: { label: 'Elite', icon: Crown, className: 'bg-gradient-to-r from-slate-800 to-black text-amber-300 ring-1 ring-amber-400/40' },
};

function LevelBadge({ level }) {
  const cfg = LEVEL_BADGE[level] || LEVEL_BADGE.standard;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.className}`}>
      <Icon className="w-3 h-3"/> {cfg.label}
    </span>
  );
}

function LimitRow({ label, used, max, unit = '€', unlimitedLabel = 'Illimité' }) {
  // max can be null/0/Infinity-like for unlimited
  const isUnlimited = !max || max <= 0 || max >= 999_999_999;
  const pct = isUnlimited ? 0 : Math.min(100, (used / max) * 100);
  const color = isUnlimited ? '' : pct >= 90 ? '[&>div]:bg-rose-500' : pct >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500';
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="font-medium">
          {isUnlimited
            ? <span className="text-emerald-600 dark:text-emerald-400">{unlimitedLabel}</span>
            : <><strong>{(used || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</strong> / {max.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} {unit}</>
          }
        </span>
      </div>
      <Progress value={isUnlimited ? 0 : pct} className={`h-1.5 mt-1 ${color}`}/>
    </div>
  );
}

export default function Carte() {
  const { t } = useLanguage();
  const tiers = [
    { name: 'basic', label: 'Basic', price: t('card.priceFree'), features: [t('card.tierBasic1'), t('card.tierBasic2'), t('card.tierBasic3')] },
    { name: 'premium', label: 'Premium', price: t('card.pricePremium'), features: [t('card.tierPremium1'), t('card.tierPremium2'), t('card.tierPremium3')] },
    { name: 'elite', label: 'Elite', price: t('card.priceElite'), features: [t('card.tierElite1'), t('card.tierElite2'), t('card.tierElite3')] },
  ];
  const [card, setCard] = useState(null);
  const [me, setMe] = useState(null);
  const [limits, setLimits] = useState(null);
  const [showCvv, setShowCvv] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [code, setCode] = useState('');
  const [target, setTarget] = useState('premium');

  const load = async () => {
    const r = await api.get('/client/me');
    setMe(r.data.user);
    setCard(r.data.card);
    // Try to fetch detailed limits + usage
    try {
      const lim = await api.get('/client/card/limits').catch(async () => {
        if (r.data?.card?.id) return await api.get(`/client/cards/${r.data.card.id}/limits`);
        return null;
      });
      if (lim?.data) setLimits(lim.data);
    } catch { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const formatNum = (num) => num ? num.replace(/(.{4})/g, '$1 ').trim() : '•••• •••• •••• ••••';

  const upgrade = async () => {
    try {
      await api.post('/client/card/upgrade', { code });
      toast.success(t('card.upgraded'));
      setUpgrading(false); setCode('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('card.invalidCode')); }
  };

  const block = async (action) => {
    try {
      await api.post(`/client/card/${action}`);
      toast.success(action === 'block' ? t('card.blocked') : t('card.reactivated'));
      load();
    } catch (e) { toast.error(t('common.error')); }
  };

  if (!me) return <div>{t('common.loading')}</div>;

  if (!card) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">{t('card.title')}</h1>
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-4"><Sparkles className="w-7 h-7"/></div>
          <h2 className="font-semibold text-lg">{t('card.noCardAssigned')}</h2>
          <p className="text-slate-600 text-sm mt-2">{t('card.noCardAssignedDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('card.title')}</h1>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div>
          <div className={`bfi-card ${card.niveau_carte || 'basic'}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-80">BFI Groupe</div>
                <div className="text-xl font-semibold capitalize">{t('card.cardOf', { level: card.card_level || card.niveau_carte })}</div>
                {(card.card_level === 'black' || card.niveau_carte === 'elite') && card.type_carte === 'mastercard' && (
                  <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 text-[10px] uppercase tracking-widest"><Sparkles className="w-3 h-3"/> {t('landing.metalBadge')}</div>
                )}
              </div>
              <div className="w-12 h-9 bg-gradient-to-br from-yellow-300 to-yellow-600 rounded"></div>
            </div>
            <div className="mt-12 text-2xl tracking-[4px] font-mono" data-testid="card-number-display">{formatNum(card.numero_carte)}</div>
            <div className="flex items-end justify-between mt-6">
              <div>
                <div className="text-[10px] opacity-60 uppercase">{t('card.cardHolder')}</div>
                <div className="font-semibold tracking-wide">{card.titulaire}</div>
              </div>
              <div>
                <div className="text-[10px] opacity-60 uppercase">{t('card.expShort')}</div>
                <div className="font-mono">{card.date_expiration}</div>
              </div>
              <div className="text-2xl font-bold italic">{card.type_carte === 'visa' ? 'VISA' : 'MASTERCARD'}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Badge variant={card.statut === 'active' ? 'default' : 'destructive'} className="capitalize">{card.statut}</Badge>
            <LevelBadge level={card.card_level || card.niveau_carte || 'standard'}/>
            <span className="text-sm text-slate-500 dark:text-slate-400">{t('card.cvv')} : <span className="font-mono">{showCvv ? card.cvv : '•••'}</span></span>
            <Button size="sm" variant="outline" onClick={() => setShowCvv(!showCvv)} data-testid="btn-toggle-cvv">{showCvv ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</Button>
          </div>

          {/* Plafonds & usage */}
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5 dark:bg-slate-900 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><Gauge className="w-5 h-5 text-blue-700"/> {t('card.cardLimits')}</h3>
              <LevelBadge level={limits?.card_level || card.card_level || card.niveau_carte || 'standard'}/>
            </div>
            <div className="mt-4 space-y-3">
              <LimitRow
                label={t('card.dailyPayment')}
                used={limits?.usage?.payment_today ?? limits?.payment_today_used ?? 0}
                max={limits?.limits?.payment_limit_daily ?? card.payment_limit_daily}
                unlimitedLabel={t('card.unlimited')}
              />
              <LimitRow
                label={t('card.dailyWithdrawal')}
                used={limits?.usage?.withdrawal_today ?? limits?.withdrawal_today_used ?? 0}
                max={limits?.limits?.withdrawal_limit_daily ?? card.withdrawal_limit_daily}
                unlimitedLabel={t('card.unlimited')}
              />
              <LimitRow
                label={t('card.monthlyPayment')}
                used={limits?.usage?.payment_month ?? limits?.payment_month_used ?? 0}
                max={limits?.limits?.payment_limit_monthly ?? card.payment_limit_monthly}
                unlimitedLabel={t('card.unlimited')}
              />
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-4">
              💡 {t('card.limitsHint')}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {card.statut === 'active' ?
              <Button variant="outline" onClick={() => block('block')} data-testid="btn-block-card"><Lock className="w-4 h-4 mr-1"/> {t('card.blockTemporarily')}</Button> :
              <Button onClick={() => block('unblock')} data-testid="btn-unblock-card">{t('card.reactivateCard')}</Button>}
          </div>
        </div>

        <div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-semibold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-700"/> {t('card.upgradeMyCard')}</h2>
            <p className="text-slate-500 text-sm mt-1">{t('card.upgradeDesc')}</p>
            <div className="mt-4 space-y-3">
              {tiers.map((tier) => {
                const isCurrent = card.niveau_carte === tier.name;
                return (
                  <div key={tier.name} className={`p-4 rounded-xl border ${isCurrent ? 'border-blue-700 bg-blue-50' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold capitalize">{tier.label} {isCurrent && <Badge className="ml-2">{t('card.current')}</Badge>}</div>
                        <div className="text-xs text-slate-500">{tier.price}</div>
                      </div>
                      {!isCurrent && tier.name !== 'basic' &&
                        <Button size="sm" onClick={() => { setTarget(tier.name); setUpgrading(true); }} data-testid={`btn-upgrade-${tier.name}`}>{t('card.upgrade')}</Button>}
                    </div>
                    <ul className="mt-2 space-y-1 text-sm text-slate-600">
                      {tier.features.map((f, i) => <li key={i}>• {f}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={upgrading} onOpenChange={setUpgrading}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('card.upgradeTo')} <span className="capitalize">{target}</span></DialogTitle>
            <DialogDescription>{t('card.upgradeCodeDesc')}</DialogDescription>
          </DialogHeader>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('card.upgradeCode')} data-testid="input-upgrade-code"/>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgrading(false)}>{t('common.cancel')}</Button>
            <Button onClick={upgrade} className="bg-blue-700" data-testid="btn-upgrade-validate"><ShieldCheck className="w-4 h-4 mr-1"/> {t('common.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
